/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useEffect, useState } from 'react';
import { Button, Select } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import Main from 'components/layout/Main';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import { formatTimeRange } from 'core/utils/date';
import { securityDashboardRequest, securityDashboardAllTodayRequest, securitySitesRequest } from 'core/securityDashboard/securityDashboardApi';
import { setSelectedSiteIds } from 'core/securityDashboard/securityDashboardSlice';
import { ChevronDownIcon } from 'components/Elements/CustomSVGIcon';
import { buildMainToolbar } from 'core/utils/mainToolbar';
import { AppDispatch, CombinedStates } from '../../core/types';
import SecurityInfoBlock from './components/SecurityInfoBlock';
import SecurityVisitList from './components/SecurityVisitList';
import SecurityCalendar from './components/SecurityCalendar';
import type { DailyVisit } from './types';
import './securityDashboard.scss';

const SecurityDashboard: FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { userData } = useSelector((state: CombinedStates) => state.user);
    const toolbar = buildMainToolbar(userData?.role?.accessLevel);
    const navigation = [{ label: 'test', link: '/abc' }, { label: 'Item 1', link: '/abc' }];
    const backLink = (
        <Button type="link" onClick={() => console.log('Back')}>
            <LeftOutlined /> BACK
        </Button>
    );

    const { securityVisitRequests, allTodayVisits, sites, selectedSiteIds } = useSelector((state: CombinedStates) => state.securityDashboard);
    const [siteDropdownOpen, setSiteDropdownOpen] = useState(false);

    useEffect(() => {
        dispatch(securitySitesRequest());
    }, []);

    // Runs on load and again whenever the picked sites change. Keyed on the
    // IDs, not the array, so a new but equal array doesn't fetch again. No
    // sites picked means all of the officer's sites.
    const selectedSitesKey = selectedSiteIds.join(',');
    useEffect(() => {
        dispatch(securityDashboardRequest({
            page: 1,
            siteIds: selectedSiteIds,
        }));
        dispatch(securityDashboardAllTodayRequest({ siteIds: selectedSiteIds }));
    }, [selectedSitesKey]);

    const siteOptions = sites.map((site) => ({ value: site.id, label: site.name }));

    const today = moment().format('YYYY-MM-DD');
    const yesterday = moment().subtract(1, 'days').format('YYYY-MM-DD');

    const filteredArray = allTodayVisits?.filter(object => object.dates.some(dateObject => dateObject.date === today
        || (object.dates.some(date => date.date === yesterday && moment(date.start_time, 'HH:mm:ss').isAfter(moment(date.end_time, 'HH:mm:ss'))))));

    const visitsToday: DailyVisit[] = filteredArray?.map(visit => {
        const date = visit.dates.find(dateObject => dateObject.date === today || (dateObject.date === yesterday && moment(dateObject.start_time, 'HH:mm:ss').isAfter(moment(dateObject.end_time, 'HH:mm:ss'))));
        return {
            encryptedId: visit.encrypted_id,
            reference: visit.id,
            siteName: visit.site_name,
            visitorsCount: visit.visitors_count,
            visitors: visit.visitors,
            shift: date?.shift || '',
            timeSlot: formatTimeRange(date?.start_time, date?.end_time),
            areaAccess: date?.site_area_name || '',
            workDetails: visit.work_description || '',
            startDate: date?.date || '',
            status: visit.status,
            showButton: true,
        };
    }) || [];

    const allVisits: DailyVisit[] = [];

    securityVisitRequests.forEach(visit => {
        visit.dates.forEach(date => {
            const showButton = date.date === moment().format('YYYY-MM-DD') || (date.date === moment().subtract(1, 'days').format('YYYY-MM-DD') && moment(date.start_time, 'HH:mm:ss').isAfter(moment(date.end_time, 'HH:mm:ss')));
            const newVisit = {
                encryptedId: visit.encrypted_id,
                reference: visit.id,
                siteName: visit.site_name,
                visitorsCount: visit.visitors_count,
                visitors: visit.visitors,
                shift: date.shift,
                timeSlot: formatTimeRange(date.start_time, date.end_time),
                areaAccess: date.site_area_name,
                workDetails: visit.work_description || '',
                startDate: date.date,
                status: visit.status,
                showButton,
            };
            allVisits.push(newVisit);
        });
    });

    // Defensive dedupe: backend now collapses 24/7 day+night rows that overlap
    // "now" to a single current shift, but a stale cached response could still
    // contain duplicates. Key by reference + date + shift name to drop them.
    const seen = new Set<string>();
    const dedupedAllVisits = allVisits.filter(v => {
        const key = `${v.reference}|${v.startDate}|${v.shift}|${v.timeSlot}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });

    dedupedAllVisits.sort((a, b) => (moment(a.startDate).isBefore(b.startDate) ? -1 : 1));

    return (
        <Main
            className="login"
            title={<div>Dashboard</div>}
            hideSubHeader
            subHeaderTitle="Dashboard"
            subHeaderHasBackLink
            subHeaderBackLink="/"
            menuWidth={350}
            menuPlacement="right"
            toolbar={toolbar}
            floatingHeader={false}
            showFooter={false}
            footerContent=""
            floatingFooter={false}
            backLink={backLink}
            breadcrumb={navigation}>
            <div className="main-container dashboard">
                <div className="security-dashboard-container">
                    <h3 className="heading">Today</h3>
                    <div className="top-container">
                        <div className="info-block">
                            <SecurityInfoBlock visitsToday={visitsToday} />
                        </div>
                        <div className="security-calendar">
                            <SecurityCalendar visitData={dedupedAllVisits} />
                        </div>
                    </div>
                    <div className="bottom-container">
                        {/* Matches the filter row on the Sites page. */}
                        <div className="security-filter-container">
                            <div className="filter-row">
                                <Select
                                    mode="multiple"
                                    placeholder="Site"
                                    aria-label="Filter by site"
                                    value={selectedSiteIds}
                                    onChange={(siteIds: number[]) => dispatch(setSelectedSiteIds(siteIds))}
                                    options={siteOptions}
                                    optionFilterProp="label"
                                    className="filter-select site-select"
                                    allowClear
                                    maxTagCount={0}
                                    maxTagPlaceholder={(omittedValues) => (omittedValues.length === 1 ? omittedValues[0]?.label : `${omittedValues.length} Selected`)}
                                    onDropdownVisibleChange={setSiteDropdownOpen}
                                    suffixIcon={(
                                        <div className={`dropdown-handle ${siteDropdownOpen ? 'rotate' : ''}`}>
                                            <ChevronDownIcon w={20} />
                                        </div>
                                    )} />
                                <Button type="link" className="clear-button" onClick={() => dispatch(setSelectedSiteIds([]))}>
                                    Clear all
                                </Button>
                            </div>
                        </div>
                        <SecurityVisitList visitData={dedupedAllVisits} />
                    </div>
                </div>
            </div>
        </Main>
    );
};

export default SecurityDashboard;
